import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, buildWsUrl, formatApiError } from "../lib/api";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { UploadSimple, DeviceMobileCamera, X, CheckCircle, Trash } from "@phosphor-icons/react";

/**
 * SignatureCapture — applicant signature input on the public registration form.
 * Two methods:
 *   1. Upload an image file from the device.
 *   2. Generate a QR code → mobile signature pad → live sync via WebSocket.
 *
 * Emits the resulting base64 data URL via `onChange`.
 */
export default function SignatureCapture({ value, onChange }) {
  const [mode, setMode] = useState(null); // null | "upload" | "qr"
  const [session, setSession] = useState(null);
  const wsRef = useRef(null);
  const fileRef = useRef(null);

  const startQrSession = useCallback(async () => {
    try {
      const { data } = await api.post("/photo-sync/session");
      setSession(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not start sync");
    }
  }, []);

  // Open WebSocket when QR session created
  useEffect(() => {
    if (mode !== "qr" || !session?.token) return;
    const ws = new WebSocket(buildWsUrl(`/api/ws/photo-sync/${session.token}`));
    wsRef.current = ws;
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === "photo" && msg.photo_base64) {
          onChange(msg.photo_base64);
          toast.success("Signature received from device");
        }
      } catch {}
    };
    return () => {
      try { ws.close(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, session?.token]);

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("File too large (max 4MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onChange(reader.result);
      toast.success("Signature uploaded");
    };
    reader.readAsDataURL(file);
  };

  const clear = () => {
    onChange(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const switchMode = async (m) => {
    setMode(m);
    if (m === "qr" && !session) await startQrSession();
  };

  const mobileUrl = session ? `${window.location.origin}/m/sign/${session.token}` : "";

  return (
    <div className="border border-slate-200 p-5" data-testid="signature-capture">
      {value ? (
        <div className="space-y-3" data-testid="signature-preview-wrap">
          <div className="bg-white border border-slate-300 p-3 inline-block">
            <img src={value} alt="signature" className="max-h-32 max-w-full" data-testid="signature-preview" />
          </div>
          <div>
            <button type="button" onClick={clear} data-testid="signature-clear" className="sms-btn-secondary inline-flex items-center gap-2 text-xs">
              <Trash size={14} weight="bold" /> Clear & re-sign
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-4">
            <button type="button" onClick={() => switchMode("upload")} data-testid="signature-mode-upload" className={`sms-btn-secondary inline-flex items-center gap-2 ${mode === "upload" ? "border-slate-900" : ""}`}>
              <UploadSimple size={14} weight="bold" /> Upload
            </button>
            <button type="button" onClick={() => switchMode("qr")} data-testid="signature-mode-qr" className={`sms-btn-secondary inline-flex items-center gap-2 ${mode === "qr" ? "border-slate-900" : ""}`}>
              <DeviceMobileCamera size={14} weight="bold" /> Sign on phone
            </button>
            {mode && (
              <button type="button" onClick={() => setMode(null)} className="ml-auto text-slate-400 hover:text-slate-900" data-testid="signature-cancel">
                <X size={16} weight="bold" />
              </button>
            )}
          </div>

          {mode === "upload" && (
            <div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={handleUpload}
                data-testid="signature-file-input"
                className="block w-full text-sm text-slate-700 file:mr-4 file:py-2 file:px-4 file:border file:border-slate-300 file:bg-white file:font-mono-sms file:text-xs file:uppercase file:tracking-[0.1em] hover:file:bg-slate-50 cursor-pointer"
              />
              <div className="mt-2 text-[10px] font-mono-sms uppercase tracking-[0.2em] text-slate-400">
                PNG / JPG · Max 4MB
              </div>
            </div>
          )}

          {mode === "qr" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
              <div className={`p-4 flex flex-col items-center justify-center text-center ${value ? "sms-qr-success" : "sms-qr-waiting"}`}>
                {!session && <div className="font-mono-sms text-xs uppercase tracking-[0.2em] text-slate-500">Generating session…</div>}
                {session && (
                  <>
                    <div className="bg-white p-2 border border-slate-900">
                      <QRCodeSVG value={mobileUrl} size={150} level="M" data-testid="signature-qr" />
                    </div>
                    <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-600 mt-3">
                      Scan with phone
                    </div>
                  </>
                )}
              </div>
              <div className="text-sm text-slate-600 leading-relaxed">
                <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">How it works</div>
                Open your phone camera. Scan the QR. Sign on the touchscreen. The signature appears here automatically. <CheckCircle size={14} weight="duotone" className="inline text-[var(--sms-success)]" />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
